import React, { useState, useEffect } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Chip,
  Tooltip,
  Typography,
  Box,
} from "@material-ui/core";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileCopy as CopyIcon,
  Add as AddIcon,
  Code as CodeIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import toastUtils from "../../utils/toastUtils";
import ConfirmationModal from "../ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  title: {
    color: theme.palette.primary.main,
  },
  table: {
    minWidth: 650,
  },
  enabledChip: {
    backgroundColor: "#4caf50",
    color: "white",
  },
  disabledChip: {
    backgroundColor: "#f44336",
    color: "white",
  },
  tokenCell: {
    fontFamily: "monospace",
    fontSize: "0.875rem",
  },
}));

const WebChatList = ({ onEdit, onRefresh }) => {
  const classes = useStyles();
  const [webChats, setWebChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedWebChat, setSelectedWebChat] = useState(null);

  useEffect(() => {
    loadWebChats();
  }, []);

  const loadWebChats = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/webchat/configs");
      setWebChats(data);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToken = (token) => {
    navigator.clipboard.writeText(token);
    toastUtils.success(i18n.t("settings.webchat.list.tokenCopied"));
  };

  const handleCopyWidgetCode = (token) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";
    const widgetCode = `<!-- WebChat Widget -->
<script>
  (function() {
    var script = document.createElement('script');
    script.src = '${backendUrl}/widget/webchat.js';
    script.dataset.token = '${token}';
    script.async = true;
    document.body.appendChild(script);
  })();
</script>`;

    navigator.clipboard.writeText(widgetCode);
    toastUtils.success(i18n.t("settings.webchat.list.codeCopied"));
  };

  const handleDeleteClick = (webChat) => {
    setSelectedWebChat(webChat);
    setConfirmModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/webchat/configs/${selectedWebChat.id}`);
      toastUtils.success(i18n.t("settings.webchat.list.deleteSuccess"));
      loadWebChats();
    } catch (error) {
      toastError(error);
    } finally {
      setConfirmModalOpen(false);
      setSelectedWebChat(null);
    }
  };

  return (
    <Paper className={classes.mainPaper} elevation={1}>
      <Box className={classes.header}>
        <Typography variant="h6" className={classes.title}>{i18n.t("settings.webchat.list.title")}</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => onEdit(null)}
        >
          {i18n.t("settings.webchat.list.newWebChat")}
        </Button>
      </Box>

      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell>{i18n.t("settings.webchat.list.name")}</TableCell>
            <TableCell>{i18n.t("settings.webchat.list.status")}</TableCell>
            <TableCell>{i18n.t("settings.webchat.list.token")}</TableCell>
            <TableCell>{i18n.t("settings.webchat.list.allowedOrigins")}</TableCell>
            <TableCell align="center">{i18n.t("settings.webchat.list.actions")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={5} align="center">
                {i18n.t("settings.webchat.list.loading")}
              </TableCell>
            </TableRow>
          ) : webChats.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center">
                {i18n.t("settings.webchat.list.noWebChats")}
              </TableCell>
            </TableRow>
          ) : (
            webChats.map((webChat) => (
              <TableRow key={webChat.id}>
                <TableCell>{webChat.name}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={
                      webChat.enabled
                        ? i18n.t("settings.webchat.list.enabled")
                        : i18n.t("settings.webchat.list.disabled")
                    }
                    className={
                      webChat.enabled ? classes.enabledChip : classes.disabledChip
                    }
                  />
                </TableCell>
                <TableCell className={classes.tokenCell}>
                  {webChat.token?.substring(0, 20)}...
                </TableCell>
                <TableCell>
                  {webChat.allowedOrigins ? (
                    <Tooltip title={webChat.allowedOrigins}>
                      <Typography variant="body2" noWrap>
                        {webChat.allowedOrigins.split(",")[0]}
                        {webChat.allowedOrigins.split(",").length > 1 &&
                          ` +${webChat.allowedOrigins.split(",").length - 1}`}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("settings.webchat.list.allOrigins")}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title={i18n.t("settings.webchat.list.copyToken")}>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyToken(webChat.token)}
                    >
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={i18n.t("settings.webchat.list.copyCode")}>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyWidgetCode(webChat.token)}
                    >
                      <CodeIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={i18n.t("settings.webchat.list.edit")}>
                    <IconButton size="small" onClick={() => onEdit(webChat)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={i18n.t("settings.webchat.list.delete")}>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(webChat)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <ConfirmationModal
        title={i18n.t("settings.webchat.list.deleteConfirmTitle")}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleDeleteConfirm}
      >
        {i18n.t("settings.webchat.list.deleteConfirmMessage", {
          name: selectedWebChat?.name,
        })}
      </ConfirmationModal>
    </Paper>
  );
};

export default WebChatList;
